/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import { store } from 'redux/store';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import { TradingOffer } from 'types/TradingOffer';
import { Partner } from 'types';
import { setModal } from 'modules/_shared/redux/modalActions';
import { createTradingOfferOperation } from 'modules/trading/redux/tradingOfferOperationActions';
import { collectPublisherAuditContext } from 'modules/trading/utils/collectPublisherAuditContext';
import { addDocument } from 'modules/documents/redux/documentActions';

import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import { ScreenSizes } from 'constants/sizes';
import { AcceptTradingOfferModalContextType } from '../types/AcceptTradingOfferModalContextType';

const defaultContextValue: AcceptTradingOfferModalContextType = {
  data: {
    offer: {} as TradingOffer,
    partnerBuyer: null,
    purchaseQuantity: 0,
    currentStep: 1,
    isTermsAccepted: false,
    isIrrevocableAccepted: false,
    isFiscalAccepted: false,
    isDataVerified: false,
    isLoading: false,
    offerReference: '',
    sellerName: '',
    className: '',
    buyerDocuments: [],
  },
  actions: {
    handleCloseModal: () => {},
    handleNextStep: () => {},
    handlePrevStep: () => {},
    handleToggleTerms: () => {},
    handleToggleIrrevocable: () => {},
    handleToggleFiscal: () => {},
    handleToggleDataVerified: () => {},
    handleAddDocument: () => {},
    handleRemoveDocument: () => {},
    handleUpdatePartnerBuyerField: () => {},
    handleUpdatePartnerBuyerAddressField: () => {},
    handleConfirmPurchase: async () => {},
  },
};

export const AcceptTradingOfferModalContext =
  createContext<AcceptTradingOfferModalContextType>(defaultContextValue);

type AcceptTradingOfferModalProviderProps = {
  children: ReactNode;
  offer: TradingOffer;
  partnerBuyer: Partial<Partner> | null;
  purchaseQuantity: number;
  societyId: string;
  offerReference: string;
  sellerName: string;
  className: string;
  onSuccess?: () => void;
  size?: ScreenSizes;
};

export const AcceptTradingOfferModalProvider: FC<
  AcceptTradingOfferModalProviderProps
> = ({
  children,
  offer,
  partnerBuyer,
  purchaseQuantity,
  societyId,
  offerReference,
  sellerName,
  className,
  onSuccess,
}) => {
  const { t } = useTranslate();
  const user = useSelector((state: { user?: { _id?: string } }) => state.user);

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isIrrevocableAccepted, setIsIrrevocableAccepted] = useState(false);
  const [isFiscalAccepted, setIsFiscalAccepted] = useState(false);
  const [isDataVerified, setIsDataVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [buyerDocuments, setBuyerDocuments] = useState<File[]>([]);
  const [editablePartnerBuyer, setEditablePartnerBuyer] = useState<
    Partial<Partner> | null
  >(
    partnerBuyer
      ? {
          ...partnerBuyer,
          address: partnerBuyer.address
            ? { ...partnerBuyer.address }
            : {
                line1: '',
                zip: '',
                city: '',
                state: '',
                country: '',
              },
        }
      : null
  );

  const handleCloseModal = () => {
    store.dispatch(setModal(null));
  };

  const handleNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleAddDocument = (file: File) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];
    if (file.size > maxSize) {
      errorSwal(t('FileTooLargeMax5MB'));
      return;
    }
    if (!allowedTypes.includes(file.type)) {
      errorSwal(t('InvalidFileTypePDFJPGPNG'));
      return;
    }
    setBuyerDocuments((prev) => [...prev, file]);
  };

  const handleRemoveDocument = (index: number) => {
    setBuyerDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdatePartnerBuyerField = (field: string, value: string) => {
    setEditablePartnerBuyer((prev) =>
      prev ? { ...prev, [field]: value } : null
    );
  };

  const handleUpdatePartnerBuyerAddressField = (field: string, value: string) => {
    setEditablePartnerBuyer((prev) => {
      if (!prev) return null;
      const currentAddress = prev.address || {
        line1: '',
        zip: '',
        city: '',
        state: '',
        country: '',
      };
      return {
        ...prev,
        address: { ...currentAddress, [field]: value },
      };
    });
  };

  const handleConfirmPurchase = async () => {
    const buyerToUse = editablePartnerBuyer || partnerBuyer;
    if (!buyerToUse) return;

    try {
      setIsLoading(true);

      const buyerDocumentIds: string[] = [];

      for (const file of buyerDocuments) {
        const fileType =
          fileTypes[file.type as keyof typeof fileTypes] || 'PDF';
        const newDocument = {
          file,
          date: new Date(),
          size: file.size,
          name: file.name,
          author: user?._id,
          society: societyId,
          fileType,
          category: documentTypes.DOCUMENTS,
          subcategory: 'IDENTITY',
          description: t('IdentityDocumentForTradingOffer'),
        };
        const uploadedDoc = await store.dispatch(addDocument(newDocument));
        if (uploadedDoc?._id) {
          buyerDocumentIds.push(uploadedDoc._id);
        }
      }

      const operationData = {
        society: societyId,
        tradingOffer: offer._id,
        buyer: buyerToUse._id || partnerBuyer?._id,
        seller:
          typeof offer.partner === 'string' ? offer.partner : offer.partner._id,
        shareClass:
          typeof offer.shareClass === 'string'
            ? offer.shareClass
            : offer.shareClass?._id,
        sharesCount: purchaseQuantity,
        sharePrice: offer.sharePrice,
        buyerDocuments: buyerDocumentIds,
        participantAuditContext: collectPublisherAuditContext(),
      };

      await store.dispatch(createTradingOfferOperation(operationData));

      setIsLoading(false);
      successSwal(t('PurchaseConfirmedSuccessfully'));
      handleCloseModal();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setIsLoading(false);
      errorSwal(t('ErrorConfirmingPurchase'));
      handleCloseModal();
    }
  };

  return (
    <AcceptTradingOfferModalContext.Provider
      value={{
        data: {
          offer,
          partnerBuyer: editablePartnerBuyer ?? partnerBuyer,
          purchaseQuantity,
          currentStep,
          isTermsAccepted,
          isIrrevocableAccepted,
          isFiscalAccepted,
          isDataVerified,
          isLoading,
          offerReference,
          sellerName,
          className,
          buyerDocuments,
        },
        actions: {
          handleCloseModal,
          handleNextStep,
          handlePrevStep,
          handleToggleTerms: () => setIsTermsAccepted((prev) => !prev),
          handleToggleIrrevocable: () =>
            setIsIrrevocableAccepted((prev) => !prev),
          handleToggleFiscal: () => setIsFiscalAccepted((prev) => !prev),
          handleToggleDataVerified: () => setIsDataVerified((prev) => !prev),
          handleAddDocument,
          handleRemoveDocument,
          handleUpdatePartnerBuyerField,
          handleUpdatePartnerBuyerAddressField,
          handleConfirmPurchase,
        },
      }}
    >
      {children}
    </AcceptTradingOfferModalContext.Provider>
  );
};

export const useAcceptTradingOfferModal = () => {
  const context = useContext(AcceptTradingOfferModalContext);
  if (!context) {
    throw new Error(
      'useAcceptTradingOfferModal must be used within a AcceptTradingOfferModalProvider'
    );
  }
  return context;
};
